import classNames from "classnames";
import React, { FC, SetStateAction, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes/routes";
import { ReactComponent as CheckCircle } from "assets/svgs/check-circle.svg";
import { ReactComponent as Chat } from "assets/svgs/chat.svg";
import OnahButton from "components/OnahButton";
import { useOnahSelector } from "store/hooks";
import { reduceObject } from "helper";
import { motion } from "framer-motion";

type Props = {
  className?: string;
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  isTablet: boolean;
};

const OnboardingSideMenu: FC<Props> = ({ className, isTablet, open, setOpen }) => {
  const {onboardingSummary} = useOnahSelector(state => state.onboarding);
  const { pathname, state } = useLocation();
  const sidebarRef = useRef<any>();
  const navigate = useNavigate();

  const Nav_animation = isTablet
  ? {
      open: {
        x: 0,
        width: "272px",
        transition: {
          damping: 40,
        },
      },
      closed: {
        x: -250,
        width: 0,
        transition: {
          damping: 40,
          delay: 0.15,
        },
      },
    }
  : {
      open: {
        width: "272px",
        transition: {
          damping: 40,
        },
      },
      closed: {
        width: "272px",
        transition: {
          damping: 40,
        },
      },
    };

  const constructActiveArray = (routes: Array<Routes>) => {
    return routes.map((route) => route.toLowerCase());
  };

  const onboardingSteps = Object.freeze([
    { title: "Getting Started", url: Routes.getStarted, done: onboardingSummary!.started},
    { title: "Business Info", url: Routes.businessInfo, done: Object.values(onboardingSummary!.bizInfo).every(r => r) },
    { title: "Registration", url: Routes.businessRegistration, done: Object.values(onboardingSummary!.bizReg).every(r => r) },
    {
      title: "People",
      url: Routes.businessOnboardingPeople,
      done: Object.values(onboardingSummary!.shareholder).every(r => r),
      subMenus: [
        {
          title: "Overview",
          url: Routes.businessOnboardingPeople,
          active: constructActiveArray([
            Routes.businessOnboardingPeopleProfile,
          ]),
        },
        {
          title: state?.shareholder ?? "Shareholder",
          hide: !(pathname.includes(Routes.businessOnboardingPeopleIdentity) || pathname.includes(Routes.businessOnboardingPeopleOwnerShipStructure)),
          url: Routes.businessOnboardingPeopleIdentity,
          active: constructActiveArray([
            Routes.businessOnboardingPeopleIdentity,
            Routes.businessOnboardingPeopleAddress,
            Routes.businessOnboardingPeopleVerify,
          ]),
        },
        {
          title: "Ownership Structure",
          url: Routes.businessOnboardingPeopleOwnerShipStructure,
        },
      ],
    },
    { title: "Account", url: Routes.businessOnboardingAccount, done: onboardingSummary?.account },
    { title: "Summary", url: Routes.businessOnboardingSummary, done: Object.values(reduceObject(onboardingSummary!)).every(r => r) },
  ]);

  const handleLink = (url: any) => {
    navigate(url);
  };

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className={`lg:hidden fixed inset-0 max-h-screen z-[998] bg-black/50 ${
          open ? "block" : "hidden"
        } `}
      ></div>
      <motion.aside
        className={classNames(
          "w-[272px] max-2xl:w-[262px] py-[40px] max-2xl:py-[20px] pl-[40px]",
          "max-2xl:pl-[20px] pr-[8px] bg-white flex flex-col shadow-xl z-[999] top-0 bottom-0",
          className
        )}
        variants={Nav_animation}
        ref={sidebarRef}
        initial={{ x: isTablet ? -272 : 0 }}
        animate={open ? "open" : "closed"}
      >
        <div className="flex-1 overflow-y-auto">
          <h2 className="sticky top-0 font-acorn text-gray-placeholder font-bold text-[24px] max-2xl:text-[20px] pb-[20px] max-2xl:pb-[10px] mb-[20px] max-2xl:mb-[10px]">
            Compliance Set Up
          </h2>
          <ul className="text-gray-placeholder font-semibold text-[18px] max-2xl:text-[14px]">
            {onboardingSteps.map((step, index) => (
              <div className={classNames("mb-1 last:mb-0")} key={index}>
                <li
                  role="button"
                  className={classNames(
                    "py-[17px] max-2xl:py-[10px] px-[11px] max-2xl:px-[8px]  group",
                    pathname.toLowerCase().includes(step.url.toLowerCase())
                      ? "pending"
                      : "inactive",
                    step.done ? "done cursor-not-allowed" : "",
                    "[&.pending:not(.done)]:bg-[#EF86320D] [&.pending]:text-black space-x-[12px] flex items-center rounded-lg"
                  )}
                  onClick={() => !step.done && handleLink(step.url)}
                >
                  <span
                    className={classNames(
                      "h-[22px] w-[22px] max-2xl:h-[20px] max-2xl:w-[20px] inline-grid place-items-center text-white font-extrabold text-[14px] max-2xl:text-[12px]",
                      "bg-orange-secondary group-[.inactive]:!bg-gray-placeholder rounded-full group-[.done]:hidden"
                    )}
                  >
                    {index + 1}
                  </span>
                  <span className="group-[:not(.done)]:hidden group-[.done]:inline-block !mx-0">
                    <CheckCircle className="h-[22px] w-[22px] max-2xl:h-[20px] max-2xl:w-[20px]" />
                  </span>
                  <span>{step.title}</span>
                </li>
                {pathname.toLowerCase().includes(step.url.toLowerCase()) && (
                  <div>
                    {step.subMenus?.map((subMenu, index) => (
                      subMenu.hide ? <div/> :
                      <li
                        className={classNames(
                          "flex items-center space-x-2 ml-[10%] mb-8 max-2xl:mb-5 first:mt-8 first:max-2xl:mt-5 group",
                          pathname.toLowerCase() === subMenu.url.toLowerCase() ||
                            subMenu.active?.includes(pathname.toLowerCase())
                            ? "active"
                            : ""
                        )}
                        key={subMenu.title}
                      >
                        <div className="h-[12px] w-[12px]  max-2xl:h-[8px]  max-2xl:w-[8px] rounded-full bg-gray-placeholder group-[.active]:bg-orange-secondary" />
                        <span className="group-[.active]:text-orange-secondary">
                          {subMenu.title}
                        </span>
                      </li>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ul>
        </div>
        <div>
          <div className="mr-[32px] max-2xl:mr-[12px] mb-[80px] max-2xl:mb-[70px]">
            <p className="mb-[12px] max-2xl:mb-[10px] text-center font-semibold text-[18px] max-2xl:text-[16px]">
              Still need help?
            </p>
            <OnahButton
              className={classNames(
                "!bg-gray-secondaryCta flex items-center w-full",
                "!text-black space-x-[8px] max-2xl:space-x-[4px]"
              )}
            >
              <Chat className="w-[14px] h-[14px] max-2xl:w-[12px] max-2xl:h-[12px]" />
              <span>Contact Support</span>
            </OnahButton>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default OnboardingSideMenu;
